import { v4 as uuidv4 } from 'uuid';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  BillingType,
  ParameterSelection_RequestableParameterFragment,
  ParameterToAddInput,
  PatientDataInput,
} from '../../graphql/generated/graphql.ts';
import type { ImportFileParseResult } from '../../utils/aisImport';

export type BillingInfo = ParameterSelection_RequestableParameterFragment['billingInfos'][number];

export interface AiChatMessage {
  role: 'user' | 'assistant';
  content: string;
  traceId?: string;
  suggestedParameters?: AiSuggestedParameter[];
  feedbackGiven?: boolean;
}

export interface AiSuggestedParameter {
  labId: string;
  shortName: string;
  reason: string;
}

export interface SelectedParameter extends ParameterSelection_RequestableParameterFragment {
  billingInfo: BillingInfo;
}

interface SelectedProfile {
  id: string;
  // true only when selecting the profile actually put its diagnose into the free-text field —
  // deselecting may take back what we wrote, never what the user wrote themselves
  diagnoseInserted: boolean;
}

export interface ParamQueue {
  parameters: ParameterToAddInput[];
  selectedParameters: SelectedParameter[];
  chooseBilling: boolean;
  ignoredParameters: ParameterSelection_RequestableParameterFragment[];
}

export const initialParamQueue: ParamQueue = {
  parameters: [],
  selectedParameters: [],
  chooseBilling: false,
  ignoredParameters: [],
};

export interface LocalizationFieldValue {
  label: string;
  value: string;
}

export interface Localization {
  paramId: string;
  specimenId: string;
  location: string;
  /** true for entries the user typed instead of picking from the Material's predefined list */
  freeText: boolean;
  fieldValues: LocalizationFieldValue[];
}

// a redeemed AIS context also carries its launch id, which links the Anforderung to the AIS request, and whether the AIS owns the patient data
type PendingImport = ImportFileParseResult & {
  aisLaunchId?: string | null;
  patientDataFromAis?: boolean;
  // prefills only an AIS sends
  acute?: boolean;
  labInfo?: string | null;
  elgaOptOut?: boolean;
};

interface AppState {
  patientData: PatientDataInput | null;
  setPatientData: (patientData: PatientDataInput | null) => void;

  // biome-ignore lint/suspicious/noExplicitAny: dynamic Ant Design form data
  formData: any;
  // biome-ignore lint/suspicious/noExplicitAny: dynamic Ant Design form data
  setFormData: (formData: any) => void;

  paramQueue: ParamQueue;
  setParamQueue: (callback: (state: AppState) => ParamQueue) => void;

  // profiles the user picked themselves — a profile is never shown as selected
  // just because its parameters happen to be covered by other selections
  selectedProfiles: SelectedProfile[];
  setSelectedProfiles: (selectedProfiles: SelectedProfile[]) => void;

  parameterIdsWithUnknownBilling: string[];
  setParameterIdsWithUnknownBilling: (parameterIdsWithUnknownBilling: string[]) => void;

  diagnose: string;
  setDiagnose: (diagnose: string) => void;

  labInfo: string;
  setLabInfo: (labInfo: string) => void;

  comment: string;
  setComment: (comment: string) => void;

  acute: boolean;
  setAcute: (acute: boolean) => void;

  elgaOptOut: boolean;
  setElgaOptOut: (elgaOptOut: boolean) => void;

  additionalData: Record<string, string>;
  setAdditionalData: (additionalData: Record<string, string>) => void;

  localizations: Localization[];
  setLocalizations: (localizations: Localization[]) => void;

  additionalDataInvalidFields: string[];
  setAdditionalDataInvalidFields: (additionalDataInvalidFields: string[]) => void;

  sampledAt: string | null;
  setSampledAt: (sampledAt: string | null) => void;

  requestIdForUpdate: string;
  setRequestIdForUpdate: (requestIdForUpdate: string) => void;

  requestIdForReorder: string;
  setRequestIdForReorder: (requestIdForReorder: string) => void;

  requestIdForNew: string;
  setRequestIdForNew: (requestIdForNew: string) => void;

  openRequestsModalEnabled: boolean;
  setOpenRequestsModalEnabled: (openRequestsModalEnabled: boolean) => void;

  tempAisImportResult: PendingImport | null;
  setTempAisImportResult: (tempAisImportResult: PendingImport | null) => void;

  // only the create of the Anforderung imported with it may carry it; any later one in the tab is unlinked
  aisLaunchIdForCreate: string | undefined;
  setAisLaunchIdForCreate: (aisLaunchIdForCreate: string | undefined) => void;

  aisImportResult: PendingImport | null;
  setAisImportResult: (AisImportResult: PendingImport | null) => void;

  executeRequestCopy: boolean;
  setExecuteRequestCopy: (executeRequestCopy: boolean) => void;

  preferredBillingType: BillingType | null;
  setPreferredBillingType: (billingType: BillingType | null) => void;

  aiChat: {
    messages: AiChatMessage[];
    suggestedParameters: AiSuggestedParameter[];
    sessionId: string;
    isStreaming: boolean;
    streamedText: string;
    error: { message: string; type?: string } | null;
  };
  setAiChatMessages: (messages: AiChatMessage[]) => void;
  setAiChatSuggestedParameters: (suggestedParameters: AiSuggestedParameter[]) => void;
  setAiChatStreaming: (isStreaming: boolean) => void;
  setAiChatStreamedText: (streamedText: string) => void;
  setAiChatError: (error: { message: string; type?: string } | null) => void;
  resetAiChat: () => void;

  reset: () => void;
}

export const useAppStore = create<AppState>()(
  devtools(
    set => ({
      patientData: null,
      setPatientData: patientData => set({ patientData }),

      formData: null,
      setFormData: formData => set({ formData }),

      paramQueue: initialParamQueue,
      setParamQueue: callback => set(state => ({ paramQueue: callback(state) })),

      selectedProfiles: [],
      setSelectedProfiles: selectedProfiles => set({ selectedProfiles }),

      parameterIdsWithUnknownBilling: [],
      setParameterIdsWithUnknownBilling: parameterIdsWithUnknownBilling => set({ parameterIdsWithUnknownBilling }),

      diagnose: '',
      setDiagnose: diagnose => set({ diagnose }),

      labInfo: '',
      setLabInfo: labInfo => set({ labInfo }),

      comment: '',
      setComment: comment => set({ comment }),

      acute: false,
      setAcute: acute => set({ acute }),

      elgaOptOut: false,
      setElgaOptOut: elgaOptOut => set({ elgaOptOut }),

      additionalData: {},
      setAdditionalData: additionalData => set({ additionalData }),

      localizations: [],
      setLocalizations: localizations => set({ localizations }),

      additionalDataInvalidFields: [],
      setAdditionalDataInvalidFields: additionalDataInvalidFields => set({ additionalDataInvalidFields }),

      sampledAt: null,
      setSampledAt: sampledAt => set({ sampledAt }),

      requestIdForUpdate: '',
      setRequestIdForUpdate: requestIdForUpdate => set({ requestIdForUpdate }),

      requestIdForReorder: '',
      setRequestIdForReorder: requestIdForReorder => set({ requestIdForReorder }),

      requestIdForNew: '',
      setRequestIdForNew: requestIdForNew => set({ requestIdForNew }),

      openRequestsModalEnabled: true,
      setOpenRequestsModalEnabled: openRequestsModalEnabled => set({ openRequestsModalEnabled }),

      aisImportResult: null,
      setAisImportResult: aisImportResult => set({ aisImportResult }),

      tempAisImportResult: null,
      setTempAisImportResult: tempAisImportResult => set({ tempAisImportResult }),

      aisLaunchIdForCreate: undefined,
      setAisLaunchIdForCreate: aisLaunchIdForCreate => set({ aisLaunchIdForCreate }),

      executeRequestCopy: false,
      setExecuteRequestCopy: executeRequestCopy => set({ executeRequestCopy }),

      preferredBillingType: null,
      setPreferredBillingType: preferredBillingType => set({ preferredBillingType }),

      aiChat: {
        messages: [],
        suggestedParameters: [],
        sessionId: uuidv4(),
        isStreaming: false,
        streamedText: '',
        error: null,
      },
      setAiChatMessages: messages => set(state => ({ aiChat: { ...state.aiChat, messages } })),
      setAiChatSuggestedParameters: suggestedParameters => set(state => ({ aiChat: { ...state.aiChat, suggestedParameters } })),
      setAiChatStreaming: isStreaming => set(state => ({ aiChat: { ...state.aiChat, isStreaming } })),
      setAiChatStreamedText: streamedText => set(state => ({ aiChat: { ...state.aiChat, streamedText } })),
      setAiChatError: error => set(state => ({ aiChat: { ...state.aiChat, error } })),
      resetAiChat: () =>
        set({
          aiChat: {
            messages: [],
            suggestedParameters: [],
            sessionId: uuidv4(),
            isStreaming: false,
            streamedText: '',
            error: null,
          },
        }),

      reset: () =>
        set({
          patientData: null,
          parameterIdsWithUnknownBilling: [],
          paramQueue: initialParamQueue,
          selectedProfiles: [],
          diagnose: '',
          labInfo: '',
          comment: '',
          acute: false,
          elgaOptOut: false,
          additionalData: {},
          localizations: [],
          additionalDataInvalidFields: [],
          sampledAt: null,
          requestIdForUpdate: '',
          requestIdForReorder: '',
          requestIdForNew: '',
          openRequestsModalEnabled: true,
          formData: null,
          aisImportResult: null,
          tempAisImportResult: null,
          aisLaunchIdForCreate: undefined,
          executeRequestCopy: false,
          preferredBillingType: null,
          aiChat: {
            messages: [],
            suggestedParameters: [],
            sessionId: uuidv4(),
            isStreaming: false,
            streamedText: '',
            error: null,
          },
        }),
    }),
    { name: 'app-store' }
  )
);
